import React from "react";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./OpenLiftFAQ.css";

/**
 * OPENlift — Questions fréquentes (section 08)
 * Accordéons HTML natifs : utilisables au clavier, sans librairie supplémentaire.
 * Les réponses présentent les possibilités selon les modèles/configurations,
 * sans engagement universel ni affirmation technique non validée.
 */
const CONTENT = {
  fr: {
    eyebrow: "Les réponses avant de s'engager",
    titleStart: "Vous avez des questions ?",
    titleEnd: "Nous avons des réponses précises.",
    introduction:
      "Les conditions d'intégration dépendent de votre parc. Nous les clarifions avec votre équipe.",
    helpTitle: "Un cas spécifique ?",
    helpText: "Parlons de votre configuration.",
    cta: "Parler à un expert",
    items: [
      {
        question: "OPENlift fonctionne-t-il avec les ascenseurs de mon parc ?",
        answer:
          "Cela dépend des modèles de contrôleurs, de leurs versions et des interfaces disponibles. Notre équipe étudie les configurations de votre parc afin de confirmer les possibilités d'intégration avant tout engagement."
      },
      {
        question: "Faut-il remplacer les contrôleurs existants ?",
        answer:
          "Pas nécessairement. Selon l'équipement, une passerelle adaptée peut recueillir les informations disponibles sur le contrôleur existant. La solution d'intégration est définie au cas par cas, après vérification technique."
      },
      {
        question: "Puis-je consulter les informations à distance ?",
        answer:
          "Oui, lorsque la configuration de l'installation et vos droits d'accès le permettent. OPENlift rend consultables les états, événements et informations techniques disponibles ; leur niveau de détail varie selon le contrôleur et l'intégration."
      },
      {
        question: "L'IA remplace-t-elle le technicien ?",
        answer:
          "Non. OPENlift AI accompagne l'analyse en proposant des pistes à examiner à partir des informations disponibles. Le diagnostic final, les décisions et les interventions restent sous la responsabilité du professionnel."
      },
      {
        question: "Comment démarrer ?",
        answer:
          "Nous échangeons d'abord sur votre parc, les contrôleurs présents et vos besoins. Nous vérifions ensuite les possibilités d'intégration et pouvons organiser une démonstration. Un pilote peut être étudié selon les conditions convenues ensemble."
      }
    ]
  },
  en: {
    eyebrow: "Answers before you commit",
    titleStart: "Have questions?",
    titleEnd: "We have clear answers.",
    introduction:
      "Integration conditions depend on your fleet. We clarify them with your team.",
    helpTitle: "A specific situation?",
    helpText: "Let's discuss your configuration.",
    cta: "Talk to an expert",
    items: [
      {
        question: "Will OPENlift work with the elevators in my fleet?",
        answer:
          "It depends on controller models, versions and available interfaces. Our team reviews your configurations to confirm the integration options before any commitment."
      },
      {
        question: "Do we need to replace existing controllers?",
        answer:
          "Not necessarily. Depending on the equipment, a suitable gateway may retrieve information from an existing controller. The integration approach is determined case by case after technical review."
      },
      {
        question: "Can I access the information remotely?",
        answer:
          "Yes, when the installation setup and your access permissions allow it. OPENlift can provide remote access to available statuses, events and technical information; the level of detail depends on the controller and integration."
      },
      {
        question: "Does AI replace the technician?",
        answer:
          "No. OPENlift AI helps analyse available information and suggests avenues to investigate. The final diagnosis, decisions and interventions remain the responsibility of qualified professionals."
      },
      {
        question: "How do we get started?",
        answer:
          "We start with a discussion about your fleet, controller models and goals. We then review integration options and can arrange a demonstration. A pilot can be considered under mutually agreed conditions."
      }
    ]
  }
};

export default function OpenLiftFAQ() {
  const { t, i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "fr")
    .split("-")[0]
    .toLowerCase();
  const fallback = CONTENT[language] || CONTENT.fr;

  const getText = (key, defaultValue) =>
    t(`openliftFaq.${key}`, { defaultValue });

  const faqs = fallback.items.map((item, index) => ({
    question: getText(`items.${index}.question`, item.question),
    answer: getText(`items.${index}.answer`, item.answer)
  }));

  return (
    <section className="ol-faq" id="ol-faq" aria-labelledby="ol-faq-title">
      <div className="ol-faq__container">
        <div className="ol-faq__layout">
          <div className="ol-faq__intro">
            <p className="ol-faq__eyebrow">
              {getText("eyebrow", fallback.eyebrow)}
            </p>
            <h2 className="ol-faq__title" id="ol-faq-title">
              <span>{getText("titleStart", fallback.titleStart)}</span>
              <span className="ol-faq__title-accent">
                {getText("titleEnd", fallback.titleEnd)}
              </span>
            </h2>
            <p className="ol-faq__description">
              {getText("introduction", fallback.introduction)}
            </p>

            <div className="ol-faq__help">
              <svg
                className="ol-faq__help-icon"
                width="29"
                height="29"
                viewBox="0 0 32 32"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M7 23.5 4 28l1.4-7.2A11.7 11.7 0 0 1 4 15.2C4 8.8 9.4 4 16 4s12 4.8 12 11.2S22.6 26.5 16 26.5c-2.8 0-5.5-.8-7.6-2.3L7 23.5Z" />
                <path d="M10.5 13.5h11M10.5 18h8" />
              </svg>
              <div>
                <p className="ol-faq__help-title">
                  {getText("helpTitle", fallback.helpTitle)}
                </p>
                <p className="ol-faq__help-text">
                  {getText("helpText", fallback.helpText)}
                </p>
              </div>
            </div>

            <NavLink className="ol-faq__cta" to="/contact">
              <span>{getText("cta", fallback.cta)}</span>
              <svg
                width="21"
                height="21"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M4 12h16m-7-7 7 7-7 7" />
              </svg>
            </NavLink>
          </div>

          <div className="ol-faq__list" aria-label={language === "en" ? "Frequently asked questions" : "Questions fréquentes"}>
            {faqs.map((item, index) => (
              <details
                className="ol-faq__item"
                name="openlift-faq-accordion"
                key={`faq-${index}`}
                open={index === 0 ? true : undefined}
              >
                <summary className="ol-faq__question">
                  <span className="ol-faq__number" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span className="ol-faq__question-text">{item.question}</span>
                  <span className="ol-faq__toggle" aria-hidden="true" />
                </summary>
                <div className="ol-faq__answer">
                  <p>{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
